import { useCallback, useSyncExternalStore } from "react";
import {
  type TimerSettings,
  type PomodoroSettings,
  type TwentySettings,
  type StopwatchSettings,
  timerSettingsSchema,
  DEFAULT_TIMER_SETTINGS,
} from "../types/timer-settings";

const STORAGE_KEY = "palnia_timer_settings";

let cached: TimerSettings = loadFromStorage();

const listeners = new Set<() => void>();

function notifyListeners() {
  for (const l of listeners) {
    try { l(); } catch { /* ignore */ }
  }
}

function deepMergeDefaults(defaults: TimerSettings, partial: Record<string, unknown>): unknown {
  const pomodoro = typeof partial.pomodoro === "object" && partial.pomodoro
    ? { ...defaults.pomodoro, ...(partial.pomodoro as Record<string, unknown>) }
    : defaults.pomodoro;
  const twenty = typeof partial.twenty === "object" && partial.twenty
    ? { ...defaults.twenty, ...(partial.twenty as Record<string, unknown>) }
    : defaults.twenty;
  const stopwatch = typeof partial.stopwatch === "object" && partial.stopwatch
    ? { ...defaults.stopwatch, ...(partial.stopwatch as Record<string, unknown>) }
    : defaults.stopwatch;
  return { pomodoro, twenty, stopwatch };
}

function loadFromStorage(): TimerSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TIMER_SETTINGS;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_TIMER_SETTINGS;
    const merged = deepMergeDefaults(DEFAULT_TIMER_SETTINGS, parsed as Record<string, unknown>);
    const result = timerSettingsSchema.safeParse(merged);
    if (!result.success) return DEFAULT_TIMER_SETTINGS;
    return result.data;
  } catch {
    return DEFAULT_TIMER_SETTINGS;
  }
}

function persist(settings: TimerSettings): void {
  cached = settings;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  notifyListeners();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): TimerSettings {
  return cached;
}

/** Non-hook synchronous read — safe to call from timer hooks */
export function getTimerSettings(): TimerSettings {
  return cached;
}

export function useTimerSettings() {
  const settings = useSyncExternalStore(subscribe, getSnapshot);

  const updatePomodoro = useCallback((patch: Partial<PomodoroSettings>) => {
    persist({ ...cached, pomodoro: { ...cached.pomodoro, ...patch } });
  }, []);

  const updateTwenty = useCallback((patch: Partial<TwentySettings>) => {
    persist({ ...cached, twenty: { ...cached.twenty, ...patch } });
  }, []);

  const updateStopwatch = useCallback((patch: Partial<StopwatchSettings>) => {
    persist({ ...cached, stopwatch: { ...cached.stopwatch, ...patch } });
  }, []);

  const resetAll = useCallback(() => {
    persist(DEFAULT_TIMER_SETTINGS);
  }, []);

  return { settings, updatePomodoro, updateTwenty, updateStopwatch, resetAll };
}
